/**
 * AccountingView
 *
 * Uses the full AccountingModule from module-accounting which includes:
 * - Dashboard
 * - Clienti
 * - Fatture
 * - E-Fatture (FatturaPA)
 * - Prima Nota (Movimenti)
 * - Banca
 * - Piano Conti
 * - Scadenze
 * - Report
 */

import { useEffect, useState } from 'react';
import { AccountingModule } from '@business-assistant/module-accounting';
import { useAppShell } from '../layouts/AppShell';
import { useInvoices, useMovements } from '@business-assistant/shared';
import { useTranslation } from '../hooks';

// =============================================================================
// RE-EXPORTS (backward compatibility)
// =============================================================================

export type AccountingType =
  | 'entrate'
  | 'uscite'
  | 'non-incassate'
  | 'da-pagare'
  | 'forecast-in'
  | 'forecast-out';

export interface AccountingItem {
  id: string;
  type: AccountingType;
  title: string;
  description: string;
  time: string;
  tags: Array<{ label: string; variant?: 'warn' | 'ok' | 'blue' }>;
  prompt?: string;
}

// =============================================================================
// MAIN VIEW
// =============================================================================

interface AccountingViewProps {
  items?: AccountingItem[];
  onItemClick?: (item: AccountingItem) => void;
}

/**
 * AccountingView - Renders the full AccountingModule with all 9 sections
 */
export function AccountingView({ items: propItems, onItemClick }: AccountingViewProps) {
  const { setDrawerSectionsOverride } = useAppShell();
  const { t } = useTranslation();
  const { data: invoices = [] } = useInvoices();
  const { data: movements = [] } = useMovements();
  const [activeSection, setActiveSection] = useState('dashboard');

  // Set up sidebar with accounting sections
  useEffect(() => {
    const mainItems = [
      {
        id: 'dashboard',
        label: t('accounting.sections.dashboard'),
        hint: t('accounting.hints.dashboard'),
        icon: 'home',
        active: activeSection === 'dashboard',
        onClick: () => setActiveSection('dashboard'),
      },
      {
        id: 'invoices',
        label: t('accounting.sections.invoices'),
        hint: t('accounting.hints.invoices'),
        icon: 'wallet',
        count: invoices.length,
        active: activeSection === 'invoices',
        onClick: () => setActiveSection('invoices'),
      },
      {
        id: 'movements',
        label: t('accounting.sections.movements'),
        hint: t('accounting.hints.movements'),
        icon: 'wallet',
        count: movements.length,
        active: activeSection === 'movements',
        onClick: () => setActiveSection('movements'),
      },
      {
        id: 'bank',
        label: t('accounting.sections.banking'),
        hint: t('accounting.hints.bank'),
        icon: 'wallet',
        active: activeSection === 'bank',
        onClick: () => setActiveSection('bank'),
      },
    ];

    const reportItems = [
      {
        id: 'deadlines',
        label: t('accounting.sections.dueDates'),
        hint: t('accounting.hints.deadlines'),
        icon: 'calendar',
        active: activeSection === 'deadlines',
        onClick: () => setActiveSection('deadlines'),
      },
      {
        id: 'reports',
        label: t('accounting.sections.reports'),
        hint: t('accounting.hints.reports'),
        icon: 'wallet',
        active: activeSection === 'reports',
        onClick: () => setActiveSection('reports'),
      },
    ];

    setDrawerSectionsOverride([
      { title: t('nav.accounting'), items: mainItems },
      { title: t('common.filters'), items: reportItems },
    ]);
    // No cleanup needed - AppShell resets sections on view change
  }, [activeSection, invoices.length, movements.length, setDrawerSectionsOverride, t]);

  return <AccountingModule />;
}
